import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  IconButton,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Divider,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Email as EmailIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  FilterList as FilterListIcon,
  People as PeopleIcon,
  DeleteForever as DeleteForeverIcon,
  CalendarToday as CalendarIcon,
  Info as InfoIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import axios from 'axios';

interface DeletedAccount {
  id: string;
  userId: string;
  email: string;
  username: string;
  fullName: string;
  phoneNumber: string;
  reason: string;
  deletedAt: any;
  deletionType: string;
}

const DeletedAccountsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deletedAccounts, setDeletedAccounts] = useState<DeletedAccount[]>([]);
  const [filteredAccounts, setFilteredAccounts] = useState<DeletedAccount[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [reasonFilter, setReasonFilter] = useState('all');
  const [selectedAccount, setSelectedAccount] = useState<DeletedAccount | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [emailResults, setEmailResults] = useState<any>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [emailSubject, setEmailSubject] = useState('☕ Seni Özledik! SobCof\'a Geri Dön 🎁');
  const [emailContent, setEmailContent] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    last7Days: 0,
    last30Days: 0,
    byReason: {} as Record<string, number>,
  });

  useEffect(() => {
    fetchDeletedAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Default email content initialization (Body only for the editor)
    setEmailContent(`
<div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; border: 1px solid #eeeeee; border-radius: 12px; overflow: hidden; font-family: sans-serif;">
  <div style="background-color: #1a1a1a; color: #ffffff; padding: 40px 20px; text-align: center;">
    <h1 style="margin: 0; font-size: 30px; color: #ffffff;">Seni Özledik!</h1>
    <p style="margin: 10px 0 0 0; font-size: 18px; color: #ffffff; opacity: 0.9;">SobCof'ta Yepyeni Maceralar Başladı</p>
  </div>
  <div style="padding: 30px; color: #000000;">
    <p style="font-size: 18px; font-weight: bold; color: #000000;">Merhaba {{displayName}},</p>
    <p style="color: #333333; line-height: 1.6;">SobCof ailesini özlediğini biliyoruz. Biz de seni çok özledik! 💖 Sen yokken uygulamamıza harika özellikler ekledik:</p>
    
    <div style="margin: 25px 0;">
      <div style="background-color: #f9f9f9; padding: 15px; border-radius: 10px; border-left: 5px solid #8B4513; margin-bottom: 12px; color: #000000;">
        <strong style="color: #8B4513;">📸 Story Özelliği:</strong> <span style="color: #000000;">Artık anlık paylaşımlar yapabilirsin!</span>
      </div>
      <div style="background-color: #f9f9f9; padding: 15px; border-radius: 10px; border-left: 5px solid #8B4513; margin-bottom: 12px; color: #000000;">
        <strong style="color: #8B4513;">🎮 Canlı Rakip Eşleşme:</strong> <span style="color: #000000;">Zekanı ve hızını kanıtla!</span>
      </div>
      <div style="background-color: #f9f9f9; padding: 15px; border-radius: 10px; border-left: 5px solid #8B4513; margin-bottom: 12px; color: #000000;">
        <strong style="color: #8B4513;">🏆 Meydan Okumalar:</strong> <span style="color: #000000;">Puanları topla, ödülleri kazan!</span>
      </div>
    </div>

    <div style="text-align: center; margin: 30px 0;">
      <a href="https://sobcof.com/app" style="display: inline-block; background-color: #8B4513; color: #ffffff; padding: 15px 35px; text-decoration: none; border-radius: 30px; font-weight: bold; font-size: 18px;">Hemen Geri Dön</a>
    </div>

    <p style="text-align: center; color: #666666; margin-top: 30px;">Görüşmek üzere! ☕<br><span style="font-weight: bold; color: #8B4513;">SobCof Ekibi</span></p>
  </div>
</div>
    `.trim());
  }, []);

  useEffect(() => {
    filterAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, reasonFilter, deletedAccounts]);

  const fetchDeletedAccounts = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('adminToken');

      const response = await axios.get('https://api.sobcof.com/api/deleted-accounts', {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { limit: 10000 },
        timeout: 60000,
      });

      if (response.data.success) {
        const accounts = response.data.data;
        setDeletedAccounts(accounts);
        calculateStats(accounts);
      } else {
        setError('Silinmiş hesaplar yüklenemedi');
      }
    } catch (error: any) {
      console.error('Error fetching deleted accounts:', error);
      setError(error.response?.data?.message || 'Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (accounts: DeletedAccount[]) => {
    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    const byReason: Record<string, number> = {};

    let last7Days = 0;
    let last30Days = 0;

    accounts.forEach(account => {
      const deletedDate = account.deletedAt?.toDate ? account.deletedAt.toDate() : new Date(account.deletedAt);

      if (deletedDate >= sevenDaysAgo) last7Days++;
      if (deletedDate >= thirtyDaysAgo) last30Days++;

      const reason = account.reason || 'Belirtilmedi';
      byReason[reason] = (byReason[reason] || 0) + 1;
    });

    setStats({
      total: accounts.length,
      last7Days,
      last30Days,
      byReason,
    });
  };

  const filterAccounts = () => {
    let filtered = [...deletedAccounts];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(account =>
        account.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        account.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        account.fullName?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Reason filter
    if (reasonFilter !== 'all') {
      filtered = filtered.filter(account => account.reason === reasonFilter);
    }

    setFilteredAccounts(filtered);
  };

  const exportToCSV = () => {
    const headers = ['Email', 'Username', 'Full Name', 'Phone', 'Reason', 'Deleted At'];
    const rows = filteredAccounts.map(account => [
      account.email || '',
      account.username || '',
      account.fullName || '',
      account.phoneNumber || '',
      account.reason || '',
      formatDate(account.deletedAt),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `silinmis-hesaplar-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const exportEmailsOnly = () => {
    const emails = filteredAccounts
      .map(account => account.email)
      .filter(email => email)
      .join('\n');

    const blob = new Blob([emails], { type: 'text/plain;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `email-listesi-${new Date().toISOString().split('T')[0]}.txt`;
    link.click();
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = filteredAccounts.map(a => a.id);
      setSelectedIds(allIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const sendWinBackEmails = async () => {
    try {
      setSendingEmails(true);
      setEmailResults(null);
      const token = localStorage.getItem('adminToken');

      if (selectedIds.length === 0) {
        alert('Lütfen email gönderilecek kullanıcıları seçin');
        setSendingEmails(false);
        return;
      }

      const response = await axios.post(
        'https://api.sobcof.com/api/deleted-accounts/send-winback-emails',
        {
          userIds: selectedIds,
          subject: emailSubject,
          content: emailContent
        },
        {
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 300000,
        }
      );

      if (response.data.success) {
        setEmailResults(response.data.results);
        alert(`Başarılı! ${response.data.results.sent} kullanıcıya email gönderildi.`);
        setSelectedIds([]);
      } else {
        alert('Email gönderilirken hata oluştu');
      }
    } catch (error: any) {
      console.error('Error sending emails:', error);
      alert(error.response?.data?.message || 'Email gönderilirken hata oluştu');
    } finally {
      setSendingEmails(false);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('tr-TR') + ' ' + date.toLocaleTimeString('tr-TR');
  };

  const uniqueReasons = Array.from(new Set(deletedAccounts.map(a => a.reason || 'Belirtilmedi')));

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/admin/dashboard')} color="primary">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Silinmiş Hesaplar
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<EmailIcon />}
            onClick={() => setShowEmailDialog(true)}
            disabled={selectedIds.length === 0}
            sx={{ borderRadius: 2, px: 3, fontWeight: 700 }}
          >
            {selectedIds.length > 0 ? `(${selectedIds.length}) ` : ''}Seçilenlere Email Gönder
          </Button>
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={() => {
              const emails = filteredAccounts
                .filter(a => selectedIds.includes(a.id))
                .map(account => account.email)
                .filter(Boolean)
                .join(', ');

              if (emails) {
                navigator.clipboard.writeText(emails).then(() => {
                  alert('Seçilen emailler panoya kopyalandı');
                });
              } else {
                alert('Lütfen kopyalamak için kullanıcı seçin');
              }
            }}
            disabled={selectedIds.length === 0}
            sx={{ borderRadius: 2 }}
          >
            Emailleri Kopyala
          </Button>
          <Button
            variant="outlined"
            startIcon={<EmailIcon />}
            onClick={exportEmailsOnly}
            disabled={filteredAccounts.length === 0}
          >
            Sadece Emailleri Dışa Aktar
          </Button>
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={() => {
              const emails = filteredAccounts
                .map(account => account.email)
                .filter(Boolean)
                .join(', ');

              navigator.clipboard.writeText(emails).then(() => {
                alert('Emailler panoya kopyalandı');
              });
            }}
            disabled={filteredAccounts.length === 0}
          >
            Emailleri Kopyala
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={exportToCSV}
            disabled={filteredAccounts.length === 0}
          >
            CSV Olarak İndir
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PeopleIcon sx={{ fontSize: 40, color: '#e65100' }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#e65100' }}>
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Toplam Silinmiş Hesap
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CalendarIcon sx={{ fontSize: 40, color: '#ff6f00' }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#ff6f00' }}>
                    {stats.last7Days}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Son 7 Gün
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CalendarIcon sx={{ fontSize: 40, color: '#f57c00' }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#f57c00' }}>
                    {stats.last30Days}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Son 30 Gün
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <DeleteForeverIcon sx={{ fontSize: 40, color: '#c62828' }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#c62828' }}>
                    {filteredAccounts.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Filtrelenmiş Sonuç
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Deletion Reasons Statistics */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Silme Nedenleri
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {Object.entries(stats.byReason)
            .sort(([, a], [, b]) => b - a)
            .map(([reason, count]) => (
              <Box key={reason} sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="body2">{reason}</Typography>
                  <Chip label={count} color="error" size="small" />
                </Box>
              </Box>
            ))}
        </Box>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <TextField
              fullWidth
              placeholder="Email, username veya isim ile ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <FormControl fullWidth>
              <InputLabel>Silme Nedeni</InputLabel>
              <Select
                value={reasonFilter}
                onChange={(e) => setReasonFilter(e.target.value)}
                label="Silme Nedeni"
                startAdornment={
                  <InputAdornment position="start">
                    <FilterListIcon />
                  </InputAdornment>
                }
              >
                <MenuItem value="all">Tümü</MenuItem>
                {uniqueReasons.map(reason => (
                  <MenuItem key={reason} value={reason}>{reason}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedIds.length > 0 && selectedIds.length < filteredAccounts.length}
                      checked={filteredAccounts.length > 0 && selectedIds.length === filteredAccounts.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Username</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>İsim</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Silme Nedeni</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Silinme Tarihi</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAccounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        {searchQuery || reasonFilter !== 'all'
                          ? 'Filtreye uygun hesap bulunamadı'
                          : 'Silinmiş hesap bulunamadı'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAccounts.map((account) => (
                    <TableRow key={account.id} hover selected={selectedIds.includes(account.id)}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.includes(account.id)}
                          onChange={() => handleSelectOne(account.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {account.email || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>{account.username || 'N/A'}</TableCell>
                      <TableCell>{account.fullName || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label={account.reason || 'Belirtilmedi'}
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(account.deletedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Detayları Görüntüle">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedAccount(account);
                              setShowDetailsDialog(true);
                            }}
                          >
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Email Campaign Dialog */}
      <Dialog open={showEmailDialog} onClose={() => setShowEmailDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>📧 Toplu Email Kampanyası</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {emailResults ? (
              <Box>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Email kampanyası tamamlandı!
                </Alert>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="body1">
                      <strong>Toplam:</strong> {emailResults.total} kullanıcı
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body1" color="success.main">
                      <strong>Başarılı:</strong> {emailResults.sent} email
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body1" color="error.main">
                      <strong>Başarısız:</strong> {emailResults.failed} email
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box>
                <Alert severity="info" sx={{ mb: 3 }}>
                  Seçilen <strong>{selectedIds.length}</strong> kullanıcıya özel kampanya maili gönderilecek.
                </Alert>

                <TextField
                  fullWidth
                  label="Email Konusu"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  sx={{ mb: 3 }}
                />

                <TextField
                  fullWidth
                  label="Email İçeriği (HTML Destekli)"
                  multiline
                  rows={10}
                  value={emailContent}
                  onChange={(e) => setEmailContent(e.target.value)}
                  helperText="{{displayName}} etiketi alıcının adıyla otomatik olarak değiştirilir."
                  sx={{ mb: 2 }}
                />

                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setShowPreview(!showPreview)}
                  >
                    {showPreview ? 'Düzenlemeye Dön' : 'Önizlemeyi Göster'}
                  </Button>
                </Box>

                {showPreview && (
                  <Box sx={{
                    border: '1px solid #ddd',
                    borderRadius: 1,
                    p: 2,
                    bgcolor: '#f5f5f5',
                    maxHeight: 400,
                    overflowY: 'auto'
                  }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Önizleme:
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <div dangerouslySetInnerHTML={{
                      __html: emailContent.replace(/\{\{displayName\}\}/g, 'Samet Öztürk')
                    }} />
                  </Box>
                )}

                <Alert severity="warning" sx={{ mt: 3 }}>
                  <Typography variant="body2">
                    ⚠️ Bu işlem geri alınamaz ve tüm seçilen kullanıcılara mail gönderilecektir.
                  </Typography>
                </Alert>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowEmailDialog(false);
            setEmailResults(null);
          }}>
            {emailResults ? 'Kapat' : 'İptal'}
          </Button>
          {!emailResults && (
            <Button
              variant="contained"
              color="success"
              onClick={sendWinBackEmails}
              disabled={sendingEmails}
              startIcon={sendingEmails ? <CircularProgress size={20} /> : <EmailIcon />}
            >
              {sendingEmails ? 'Gönderiliyor...' : 'Email Gönder'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onClose={() => setShowDetailsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Hesap Detayları</DialogTitle>
        <DialogContent>
          {selectedAccount && (
            <Box sx={{ pt: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Email:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{selectedAccount.email || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Username:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{selectedAccount.username || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">İsim Soyisim:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{selectedAccount.fullName || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Telefon:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{selectedAccount.phoneNumber || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Silme Nedeni:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{selectedAccount.reason || 'Belirtilmedi'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Silinme Tarihi:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{formatDate(selectedAccount.deletedAt)}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">User ID:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>{selectedAccount.userId || 'N/A'}</Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetailsDialog(false)}>Kapat</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DeletedAccountsPage;
